import { Capacitor } from '@capacitor/core';

/**
 * La coque native, vue depuis l'application.
 *
 * Repère n'a qu'une interface : celle-ci. Elle tourne dans un navigateur, et parfois dans
 * une WebView Capacitor posée sur un téléphone — même code, même écran, mêmes tests. Ce
 * module est le seul endroit qui sache faire la différence, et tout ce qui en dépend passe
 * par lui.
 *
 * Trois règles tiennent ce dossier, et la première est la raison des deux autres :
 *
 * 1. **Rien ne s'exécute au chargement du module.** Les greffons Capacitor s'importent en
 *    haut de fichier, mais aucun ne se réveille avant qu'on l'appelle : importés depuis un
 *    navigateur ordinaire, ils rendent des implémentations « web » inertes. Une seule
 *    exception, et elle n'existe pas ici : un module qui poserait un écouteur à
 *    l'import ferait échouer les tests du web, qui ne montent aucune coque.
 * 2. **Hors coque, chaque fonction renonce en silence.** Elle ne lève pas : le navigateur
 *    n'est pas un environnement dégradé, c'est le premier des deux.
 * 3. **L'échec natif ne casse jamais l'écran.** Une notification qu'on ne peut pas
 *    enregistrer, un clavier qui ne se replie pas : ce sont des dégradations, pas des
 *    pannes. Elles se rapportent, elles ne remontent pas en exception.
 */
export type PlateformeNative = 'ios' | 'android';

/** Vrai dans la coque iOS ou Android, faux dans un navigateur. */
export function estNatif(): boolean {
  return Capacitor.isNativePlatform();
}

/**
 * La plateforme, ou `null` dans un navigateur.
 *
 * `null` plutôt que `'web'` : l'appelant qui demande cela veut savoir à quel service de
 * remise parler — FCM ou APNs — et « web » n'en est pas un troisième, c'est l'absence de
 * question.
 */
export function plateformeNative(): PlateformeNative | null {
  const nom = Capacitor.getPlatform();
  return nom === 'ios' || nom === 'android' ? nom : null;
}
